import { useTranslation } from "react-i18next"
import { CaretDown, Check } from "@phosphor-icons/react"
import { Button } from "@/components/ui/button"
import { Chip } from "@/components/ui/chip"
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { type Language } from "@/modules/patients/services/__generated__/PatientFragment.graphql"
import {
  useWaitingRoomFilters,
  useWaitingRoomFiltersContext,
} from "@/modules/waiting-room/contexts/waiting-room-filters-context"
import { LanguageValues } from "@/types/__generated__/union-types"

const LanguagesFilter = () => {
  const { t } = useTranslation()

  const { filters } = useWaitingRoomFilters()
  const setFilters = useWaitingRoomFiltersContext((state) => state.setFilters)

  const languages = filters.languages || []
  const languageCount = languages.length

  const handleCheckChange = (item: Language) => {
    const index = languages.indexOf(item)
    const newLanguages = [...languages]

    if (index < 0) newLanguages.push(item)
    else newLanguages.splice(index, 1)

    if (newLanguages.length > 0) setFilters({ languages: newLanguages.map((item) => item) })
    else setFilters({ languages: null })
  }

  const handleResetLanguages = () => {
    setFilters({ languages: null })
  }

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger
        asChild
        className="aria-checked:border-on-primary-surface aria-checked:bg-primary-surface aria-checked:text-on-primary-surface aria-checked:hover:enabled:bg-primary-surface-hovered aria-checked:focus:enabled:border-on-primary-surface outline-hidden hover:cursor-pointer">
        <Chip data-testid="language-filter-trigger" className="h-8 gap-0.5" checked={languageCount > 0} type="button">
          <div className={`overflow-hidden transition-all duration-200 ${languageCount > 0 ? "w-5" : "w-0"}`}>
            <Check size={20} />
          </div>
          <span className="px-1.5">{t("common.language") + (languageCount > 0 ? ` (${languageCount})` : ``)}</span>
          <CaretDown size={20} />
        </Chip>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        side="bottom"
        align="start"
        data-testid="language-filter-content"
        className="absolute -top-1 grid w-36.5 gap-2 rounded-md border-none p-2 leading-5">
        <div className="grid gap-0.5">
          {LanguageValues.map((item) => (
            <DropdownMenuCheckboxItem
              onSelect={(e) => e.preventDefault()}
              className="h-9"
              key={item}
              checked={!!languages.find((language) => language === item)}
              data-testid={`menu-language-${item.toLowerCase()}`}
              onCheckedChange={() => handleCheckChange(item)}>
              {t(`scheduling.waitingRoom.filters.languageFilter.${item.toLocaleLowerCase()}`)}
            </DropdownMenuCheckboxItem>
          ))}
        </div>
        <DropdownMenuSeparator className="bg-border -mx-2 my-0" />
        <div className="flex justify-end">
          <Button
            onClick={handleResetLanguages}
            data-testid="reset-language-filter"
            variant="tertiary"
            className="h-9 rounded-full px-2 py-0 leading-5 font-medium">
            {t("common.button.reset")}
          </Button>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export { LanguagesFilter }
